/**
 * Reading zeecore walkaround-check rows across the before/after-drive change.
 *
 * zeecore used to label a performed check `before_drive` / `after_drive`,
 * comparing it with `first_drive_at` — which was really the shift start
 * (clocking on), not the first drive, so the split was meaningless. It now
 * reports `checked` and `shift_start_at`. The app and zeecore deploy
 * separately, so these helpers accept either shape; the legacy names can go
 * once both are deployed.
 */

import { formatSuppliedClock, formatUkTime } from './ukTime';
import { UNKNOWN_CELL } from './uiText';

/**
 * `unverified`: a clock-on by someone whose tachograph card is not on Samsara's
 * driver list, so they cannot be confirmed as a driver. Listed, not counted.
 */
export type WalkaroundCheckStatus =
  | 'checked'
  | 'missing'
  | 'no_drive'
  | 'unverified'
  | 'before_drive'
  | 'after_drive';

export function isCheckedStatus(status: string | null | undefined): boolean {
  return status === 'checked' || status === 'before_drive' || status === 'after_drive';
}

export function shiftStartOf(row: { shift_start_at?: string | null; first_drive_at?: string | null }): string | null {
  return row.shift_start_at ?? row.first_drive_at ?? null;
}

export function checkedCountOf(kpis: { checked?: number; before_drive?: number; after_drive?: number } | null | undefined): number {
  if (!kpis) return 0;
  if (typeof kpis.checked === 'number') return kpis.checked;
  return Number(kpis.before_drive || 0) + Number(kpis.after_drive || 0);
}

/**
 * A walkaround check, shift or defect time as "HH:MM" UK time. zeecore sends the
 * real instant (`*_instant`: Aquarius times read through their declared
 * mapping); an older or cached response without it shows the stored Aquarius
 * digits as written. `fallback` is shown when neither is readable.
 */
export function formatWalkaroundTimeHm(
  instant: string | null | undefined,
  stored: string | null | undefined,
  fallback: string = UNKNOWN_CELL,
): string {
  return instant ? formatUkTime(instant, {}, fallback) : formatSuppliedClock(stored, { hm: true }, fallback);
}
